import React, { useMemo } from 'react';
import Button from '../Button';
import EmptyState from '../EmptyState';
import { READING, READING_WORDING as RW, figureOrDash, reading } from '../../domain/reading';
import FamilyBalance from './FamilyBalance';
import { useMoneyFormat } from '../../context/LocaleContext';
import { familyDisplayName } from '../../domain/people';
import useNames from '../../hooks/useNames';
import { familyPickerOrder } from '../../domain/familyAccount';
import s from './families.module.css';
import t from '../SimpleTable.module.css';

// `billingContactFor` is `useFamilies().billingContactFor`: the one resolver
// for "who does this family's invoice go to", shared with the family detail
// card and the invoice itself, so no two screens name different people. The
// Family Contact column is that person (SPEC.md §3.1), from `familyContacts`
// (#319).
//
// ── In MMS's order: by Family, ascending (#467) ────────────────────────────
//
// MMS's Family Accounts table opens sorted on its Family column (`Family ↑`).
// Sorted here by the name the row shows, through the core's `labelOrder` — the
// order the Add Transaction family picker and both phones' lists use. Stable,
// so two families with one name keep a fixed order.
//
// ── Nothing said before it is read (#629) ──────────────────────────────────
//
// `reads` says which of the families, students and ledger have answered. The
// owed and prepaid figures, each row's student count and balance, and the
// empty state wait for theirs — `reading`, src/domain/reading.js.
export default function FamilyList({
  families,
  // Defaulted to read for this file's tests, which hand it lists.
  reads = { families: true, students: true, ledger: true },
  // Which of them stopped before answering (#647).
  failures = {},
  totalOwed, totalPrepaid,
  balanceFor, studentCountFor, billingContactFor, onOpenFamily, onChargeCategories,
  onAddStudent = null,
}) {
  const formatMoney = useMoneyFormat();
  const names = useNames();
  const shown = useMemo(
    () => familyPickerOrder(families.map((f) => familyDisplayName(f))).map((i) => families[i]),
    [families],
  );
  return (
    <>
      <div>
        <div className={s.head}>You&rsquo;re owed {figureOrDash(reads.ledger, formatMoney(totalOwed))}</div>
        <div className={s.sub}>
          Prepaid balance: {figureOrDash(reads.ledger && reads.families, formatMoney(totalPrepaid))}
        </div>
      </div>

      <div className={s.bar}>
        <Button variant="secondary" onClick={onChargeCategories}>Charge Categories</Button>
      </div>

      {families.length === 0 && !reads.families ? (
        <EmptyState reading={failures.families ? READING.failed : READING.unread} />
      ) : families.length === 0 ? (
        <div className={t.wrap}>
          {/* MMS's empty Family Accounts tab carries + Add Student under the
              same sentence, since adding a student is the only way a family
              account comes to exist (#630). It opens the wizard Students' + Add
              New opens. */}
          <EmptyState
            reading={reading(reads.families, families.length)}
            title={RW['families.emptyTitle']}
            description={RW['families.emptyDescription']}
            actionLabel={onAddStudent ? '+ Add Student' : undefined}
            onAction={onAddStudent}
          />
        </div>
      ) : (
        <div className={t.wrap}>
          <table className={t.table}>
            <thead><tr><th>Family</th><th>Family Contact</th><th>Students</th><th>Balance</th></tr></thead>
            <tbody>
              {shown.map((fam) => (
                <tr key={fam.id}>
                  <td>
                    <button className={s.link} onClick={() => onOpenFamily(fam.id)}>
                      {familyDisplayName(fam)}
                    </button>
                  </td>
                  <td><BilledContact family={fam} contact={billingContactFor?.(fam.id)} names={names} /></td>
                  <td>{figureOrDash(reads.students, studentCountFor(fam.id))}</td>
                  <td>{reads.ledger ? <FamilyBalance minor={balanceFor(fam.id)} /> : figureOrDash(false)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// The person the invoice is addressed to, and their email under the name where
// there is one; a dash for a family with nobody to bill.
//
// In the Parent Name Format, as Package Balances names the same person (#632):
// which format applies is a property of who is named (useNames.js);
// `contactCardName` is for a card's heading (FamilyDetail.jsx). MMS's Family
// Accounts column shows the contact's email alone, so the name above it is ours.
// Both phones take the same formatter.
function BilledContact({ family, contact, names }) {
  if (!contact) return '–';
  return (
    <>
      <div>{names.familyContact(family, contact)}</div>
      {contact.email ? <div className={s.email}>{contact.email}</div> : null}
    </>
  );
}
